import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  InfiniteScroll,
  LoadingSpinner,
  Popover,
  PopoverContentNoPortal,
  PopoverTrigger,
  TooltipProvider,
  TruncatedText,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useInfiniteSelectShapes } from '../hooks/useInfiniteSelectShapes';
import { ShapeSelectType } from '../utils';

export interface ShapeSelectProps {
  id?: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  allowedGeometryTypes?: string[];
  className?: string;
}

export function ShapeSelect({
  id,
  value,
  onChange,
  allowedGeometryTypes,
  className,
}: ShapeSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { messages } = useLocalization();
  const { filteredFlattenedShapes, isLoading, flattenedShapes } =
    useInfiniteSelectShapes(search, allowedGeometryTypes);

  const shape = useMemo(() => {
    return flattenedShapes?.find((shape) => shape.id === value);
  }, [flattenedShapes, value]);

  // Calculate dynamic height based on content
  const calculatedHeight = useMemo(() => {
    const COMMAND_INPUT_HEIGHT = 40;
    const COMMAND_ITEM_HEIGHT = 32;
    const LOADING_SPINNER_HEIGHT = 68;
    const COMMAND_EMPTY_HEIGHT = 68;
    const PADDING_AND_BORDERS = 10;
    const MAX_HEIGHT = 300;

    let contentHeight = COMMAND_INPUT_HEIGHT + PADDING_AND_BORDERS;

    if (isLoading) {
      // Add loading spinner height
      contentHeight += LOADING_SPINNER_HEIGHT;
    } else if (!filteredFlattenedShapes.length) {
      // Add empty state height
      contentHeight += COMMAND_EMPTY_HEIGHT;
    } else {
      // Add height for displayed shapes
      contentHeight += filteredFlattenedShapes.length * COMMAND_ITEM_HEIGHT;
    }

    return Math.min(contentHeight, MAX_HEIGHT);
  }, [filteredFlattenedShapes.length, isLoading]);

  // Get the appropriate empty state message based on allowedGeometryTypes
  const getEmptyStateMessage = () => {
    if (
      !allowedGeometryTypes ||
      allowedGeometryTypes.length === 0 ||
      allowedGeometryTypes.length >= 2
    ) {
      return messages.shapeSelectNoResults;
    }

    const singleType = allowedGeometryTypes[0].toLowerCase();

    switch (singleType) {
      case 'polygon':
        return messages.shapeSelectNoResultsPolygon;
      case 'linestring':
        return messages.shapeSelectNoResultsLines;
      case 'point':
        return messages.shapeSelectNoResultsPoints;
      case 'circle':
        return messages.shapeSelectNoResultsCircles;
      default:
        return messages.shapeSelectNoResults;
    }
  };

  return (
    <TooltipProvider>
      <Popover
        open={open}
        onOpenChange={() => {
          setOpen(!open);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            onClick={() => setOpen(!open)}
            className={cn('w-full justify-between pr-3', className)}
          >
            {shape && (
              <TruncatedText
                text={shape?.name ? shape.name : ''}
                className="text-left"
              />
            )}{' '}
            {!shape && value === 'Shape drawn in MDM' && (
              <TruncatedText
                text={'Shape drawn in MDM'}
                className="text-left"
              />
            )}
            {!shape && value !== 'Shape drawn in MDM' && (
              <span>{messages.shapeSelectText}</span>
            )}
            <span className="flex-1"></span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContentNoPortal
          align="start"
          className="w-full p-0 overflow-hidden"
          style={{ height: `${calculatedHeight}px` }}
        >
          <Command shouldFilter={false} className="flex flex-col h-full">
            <CommandInput
              placeholder={messages.shapeSelectSearchText}
              onValueChange={(value) => {
                setSearch(value);
              }}
              value={search}
            />
            {!isLoading && (
              <CommandEmpty>{getEmptyStateMessage()}</CommandEmpty>
            )}
            {isLoading && <LoadingSpinner size="small"></LoadingSpinner>}
            {!isLoading && flattenedShapes.length > 0 && (
              <InfiniteScroll
                items={filteredFlattenedShapes}
                limit={30}
                observeResize={true}
                renderItem={(shapeSelect: ShapeSelectType) => {
                  return (
                    <CommandItem
                      key={shapeSelect.id}
                      disabled={shapeSelect.type === 'FeatureCollection'}
                      value={shapeSelect.id}
                      onSelect={(currentValue) => {
                        onChange(currentValue === value ? '' : currentValue);
                        setOpen(false);
                      }}
                    >
                      <div className="flex justify-between w-full">
                        <div className="flex min-w-0 max-w-[85%]">
                          <span>
                            {new Array(shapeSelect.level + 1).join(
                              '\u00A0\u00A0\u00A0'
                            )}
                          </span>
                          <div className="px-2 flex-shrink-0">
                            <shapeSelect.icon />
                          </div>
                          <TruncatedText
                            text={shapeSelect?.name ? shapeSelect.name : ''}
                            maxWidth="300px"
                          />
                        </div>
                        <Check
                          className={cn(
                            'mr-1 h-4 w-4 flex-shrink-0',
                            value === shapeSelect.id
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                      </div>
                    </CommandItem>
                  );
                }}
                className="flex-1 min-h-0 p-0 border rounded-lg"
              ></InfiniteScroll>
            )}
          </Command>
        </PopoverContentNoPortal>
      </Popover>
    </TooltipProvider>
  );
}
